import { createContext, useContext, useEffect, useState } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
  RecaptchaVerifier,
} from "firebase/auth";
import { auth } from "../firebase";
import { Users } from "../lib/client";

// Crear un contexto compartido para autenticación e idioma
const userAuthContext = createContext();

export function UserAuthContextProvider({ children }) {
  // Estados para autenticación
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userDataLoading, setUserDataLoading] = useState(false);
  const [authError, setAuthError] = useState(null);
  const userApi = new Users();

  // Estado para el idioma
  const [currentLanguage, setCurrentLanguage] = useState("en");

  // Funciones de autenticación
  function logIn(email, password) {
    setAuthError(null);
    return signInWithEmailAndPassword(auth, email, password).catch((error) => {
      setAuthError(error);
      throw error;
    });
  }

  function signUp(email, password) {
    setAuthError(null);
    return createUserWithEmailAndPassword(auth, email, password).catch(
      (error) => {
        setAuthError(error);
        throw error;
      }
    );
  }

  function logOut() {
    return signOut(auth)
      .then(() => {
        // Limpiar el estado del usuario al cerrar sesión
        setUser(null);
        setAuthError(null);
      })
      .catch((error) => {
        setAuthError(error);
        throw error;
      });
  }

  function googleSignIn() {
    setAuthError(null);
    const googleAuthProvider = new GoogleAuthProvider();
    return signInWithPopup(auth, googleAuthProvider).catch((error) => {
      setAuthError(error);
      throw error;
    });
  }

  function setUpRecaptha(number) {
    try {
      const recaptchaVerifier = new RecaptchaVerifier(
        "recaptcha-container",
        {},
        auth
      );
      recaptchaVerifier.render();
      return recaptchaVerifier;
    } catch (error) {
      console.error("Error al configurar reCAPTCHA:", error);
      throw error;
    }
  }

  // Función para cambiar el idioma
  function changeLanguage(lang) {
    if (lang && (lang === "en" || lang === "es")) {
      setCurrentLanguage(lang);
      localStorage.setItem("userLanguage", lang);
    } else {
      console.warn("Idioma no soportado:", lang);
    }
  }

  // Efecto para cargar la preferencia de idioma guardada
  useEffect(() => {
    const savedLanguage = localStorage.getItem("userLanguage");
    if (savedLanguage && (savedLanguage === "en" || savedLanguage === "es")) {
      setCurrentLanguage(savedLanguage);
    }
  }, []);

  // Efecto para manejar la autenticación con mejor gestión de errores
  useEffect(() => {
    setLoading(true);
    let authUnsubscribe;

    authUnsubscribe = onAuthStateChanged(
      auth,
      async (currentuser) => {
        try {
          // Si no hay usuario, limpiar estado y terminar carga
          if (!currentuser) {
            setUser(null);
            setLoading(false);
            return;
          }

          // Establecer usuario básico inmediatamente
          setUser(currentuser);

          // Luego obtener datos adicionales
          setUserDataLoading(true);
          await fetchUserData(currentuser.uid);
        } catch (error) {
          console.error("Error en autenticación:", error);
          setAuthError(error);
        } finally {
          setLoading(false);
          setUserDataLoading(false);
        }
      },
      (error) => {
        console.error("Error en la suscripción de autenticación:", error);
        setAuthError(error);
        setLoading(false);
      }
    );

    return () => {
      if (authUnsubscribe) authUnsubscribe();
    };
  }, []);

  // Función para obtener datos adicionales del usuario con mejor manejo de errores
  async function fetchUserData(uid) {
    if (!uid) return;

    try {
      const userData = await userApi.getUserByUid(uid);

      if (userData) {
        // Actualizar el objeto de usuario con datos adicionales
        setUser((prevUser) => {
          if (prevUser && prevUser.uid === uid) {
            return {
              ...prevUser,
              type: userData.type || "user", // Usar un valor predeterminado
              displayName: userData.displayName || prevUser.displayName || "",
              role: userData.role || "user",
              // Otros datos que puedas necesitar
            };
          }
          return prevUser;
        });
      } else {
        // Si no hay datos pero el usuario está autenticado, asegurar un tipo predeterminado
        setUser((prevUser) => {
          if (prevUser && prevUser.uid === uid) {
            return { ...prevUser, type: "user" };
          }
          return prevUser;
        });
        console.warn(
          "No se encontraron datos adicionales para el usuario:",
          uid
        );
      }
    } catch (error) {
      console.error("Error al obtener datos del usuario:", error);
      // No interrumpir la autenticación por un error en datos adicionales
      // Establecer valores predeterminados si es necesario
      setUser((prevUser) => {
        if (prevUser && prevUser.uid === uid) {
          return { ...prevUser, type: "user" };
        }
        return prevUser;
      });
    }
  }

  // Valor del contexto
  const value = {
    user,
    loading,
    userDataLoading,
    authError,
    authVerified: !!user, // Simplificar la verificación
    logIn,
    signUp,
    logOut,
    googleSignIn,
    setUpRecaptha,
    fetchUserData, // Exponer esta función para actualizaciones manuales
    currentLanguage,
    changeLanguage,
  };

  return (
    <userAuthContext.Provider value={value}>
      {children}
    </userAuthContext.Provider>
  );
}

export function useUserAuth() {
  return useContext(userAuthContext);
}
